import { useLocale, useTranslations } from 'next-intl';
import React from 'react';

import { useDomainConfiguration } from '@/components/DomainConfigurationProvider';

import { getLanguageNameFromLocale } from '@cineamo/ui/helpers/locale-helper';
import { notEmptyValue } from '@cineamo/ui/helpers/type-helper';
import { currencyFormatterUSD } from '@cineamo/ui/src/helpers/currency-helper';

import MovieDetailsTable, { MovieDetailRow } from '@cineamo/composables/src/movie/MovieDetailsTable';

/**
 * Wrapper component that prepares comprehensive movie details and renders MovieDetailsTable.
 * This adapter maintains backward compatibility while using the shared MovieDetailsTable component.
 */
function MovieInfo(): React.JSX.Element {
    const {
        movie: {
            releaseDate: movieReleaseDate,
            originalLanguage: movieOriginalLanguage,
            productionCountries: movieProductionCountries
        },
        movieDto: { originalTitle: movieOriginalTitle, status: movieStatus, budget: movieBudget, revenue: movieRevenue }
    } = useDomainConfiguration();
    const locale = useLocale();

    const t = useTranslations();

    const originalLanguage = movieOriginalLanguage
        ? getLanguageNameFromLocale(movieOriginalLanguage, locale)
        : undefined;

    const details: MovieDetailRow[] = [
        movieOriginalTitle && { label: t('movie.original-title'), value: movieOriginalTitle },
        movieStatus && { label: t('movie.status.label'), value: t(`movie.status.${movieStatus}`) },
        movieReleaseDate && { label: t('movie.release-date'), value: movieReleaseDate },
        originalLanguage && { label: t('movie.original-language'), value: originalLanguage },
        movieProductionCountries && {
            label: t('movie.production-country'),
            value: movieProductionCountries
        },
        movieBudget > 0 && { label: t('movie.budget'), value: currencyFormatterUSD.format(movieBudget) },
        movieRevenue > 0 && { label: t('movie.revenue'), value: currencyFormatterUSD.format(movieRevenue) }
    ].filter(notEmptyValue);

    return (
        <MovieDetailsTable
            details={details}
            variant="compact"
        />
    );
}

export default MovieInfo;
